import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { useTranslation } from 'react-i18next';

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-background-200/70">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left cursor-pointer group"
      >
        <span className="text-base font-medium text-foreground-900 pr-4 group-hover:text-primary-600 transition-colors">
          {question}
        </span>
        <span className={`flex-shrink-0 w-8 h-8 rounded-full border border-background-300 flex items-center justify-center transition-all duration-200 ${open ? 'bg-primary-500 border-primary-500' : 'group-hover:border-foreground-400'}`}>
          <i className={`text-sm transition-all duration-200 ${open ? 'ri-subtract-line text-background-50' : 'ri-add-line text-foreground-600'}`}></i>
        </span>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${
          open ? 'max-h-96 pb-5' : 'max-h-0'
        }`}
      >
        <p className="text-sm text-foreground-600 leading-relaxed pr-4">
          {answer}
        </p>
      </div>
    </div>
  );
}

export default function Home() {
  const { t } = useTranslation();

  const faqs = [
    {
      q: 'Was ist ein OOPBuy Spreadsheet?',
      a: 'Ein OOPBuy Spreadsheet ist eine umfassende, strukturierte Excel-Liste, die Produkte, Preise und Kategorien für das Shopping auf OOPBuy-Plattformen übersichtlich darstellt. Es hilft deutschen Nutzern, schnell Produkte zu finden, Preise zu vergleichen und informierte Kaufentscheidungen zu treffen – alles an einem zentralen Ort.',
    },
    {
      q: 'Ist das OOPBuy Spreadsheet kostenlos?',
      a: 'Ja, das OOPBuy Spreadsheet ist vollständig kostenlos zugänglich. Du kannst die Produktlisten, Kategorien und Preisübersichten ohne versteckte Kosten einsehen und für deine Shopping-Recherche nutzen.',
    },
    {
      q: 'Wie funktioniert das OOPBuy Spreadsheet?',
      a: 'Das OOPBuy Spreadsheet funktioniert wie eine intelligente Produktdatenbank. Du öffnest die Liste, wählst eine Kategorie aus – zum Beispiel Sneaker oder Fashion – und siehst sofort die verfügbaren Produkte mit Preisen, Bewertungen und wichtigen Details. Von dort aus kannst du direkt zum gewünschten Produkt navigieren.',
    },
    {
      q: 'Ist das OOPBuy Spreadsheet sicher?',
      a: 'Absolut. Das OOPBuy Spreadsheet selbst enthält keine schädlichen Inhalte. Es ist eine reine Informationsquelle, die dir hilft, Produkte zu vergleichen. Wir empfehlen jedoch, immer die Seriosität der verlinkten Shops zu prüfen und sichere Zahlungsmethoden zu verwenden.',
    },
    {
      q: 'Welche Kategorien finde ich im OOPBuy Spreadsheet?',
      a: 'Das Spreadsheet deckt zahlreiche Kategorien ab: Shoes, Hoodies & Sweaters, T-Shirts, Jackets, Pants & Shorts, Headwear, Sets, Unterwäsche, Jersey, Accessories, Bags, Belts, Perfumes und Other Stuff. Jede Kategorie enthält eine kuratierte Auswahl der besten Produkte.',
    },
    {
      q: 'Kann ich das OOPBuy Spreadsheet aus Deutschland nutzen?',
      a: 'Ja, das OOPBuy Spreadsheet ist speziell für den deutschen Markt optimiert. Alle Informationen sind auf Deutsch verfügbar, und die Produktlisten berücksichtigen die Bedürfnisse deutscher Käufer – inklusive Versandinformationen und relevanter Preiskategorien.',
    },
    {
      q: 'Wie oft wird das OOPBuy Spreadsheet aktualisiert?',
      a: 'Das OOPBuy Spreadsheet wird regelmäßig aktualisiert, um sicherzustellen, dass die Produktlisten, Preise und Verfügbarkeiten stets aktuell sind. Neue Produkte und Kategorien werden fortlaufend ergänzt.',
    },
    {
      q: 'Brauche ich spezielle Software für das OOPBuy Spreadsheet?',
      a: 'Nein, du benötigst keine spezielle Software. Das OOPBuy Spreadsheet ist browserbasiert und kann auf jedem Gerät mit Internetzugang geöffnet werden – ob Desktop, Tablet oder Smartphone.',
    },
    {
      q: 'Gibt es eine OOPBuy Spreadsheet Alternative?',
      a: 'Es gibt verschiedene Alternativen, aber das OOPBuy Spreadsheet zeichnet sich durch seine umfassende Produktabdeckung, die deutsche Sprache und die benutzerfreundliche Struktur aus. Andere Quellen bieten oft nur Teilinformationen oder sind nicht für den deutschen Markt optimiert.',
    },
    {
      q: 'Wie vermeide ich Fake-Produkte beim OOPBuy Shopping?',
      a: 'Das OOPBuy Spreadsheet hilft dir, indem es geprüfte und bewertete Quellen auflistet. Achte zusätzlich auf Verkäuferbewertungen, Produktbilder und detaillierte Beschreibungen. Bei Unsicherheiten empfehlen wir, nur von verifizierten Händlern zu kaufen.',
    },
  ];

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'OOPBuy Spreadsheet Deutschland',
    url: 'https://oopbuypread.com/',
    description: 'Die komplette OOPBuy Spreadsheet Übersicht für Deutschland. Finde Produkte, Preise, Kategorien und sichere Shopping Tipps.',
    inLanguage: 'de-DE',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: 'https://oopbuypread.com/search?q={search_term_string}',
      },
      'query-input': 'required name=search_term_string',
    },
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'OOPBuy Spreadsheet',
    url: 'https://oopbuypread.com/',
    logo: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/5133451b25e408e4a25e4df97e05add8.jpeg',
    description: 'Unabhängige deutschsprachige Informationsplattform rund um das OOPBuy Spreadsheet. Die zentrale Anlaufstelle für China Shopping aus Deutschland.',
    sameAs: [
      'https://oopbuypread.com/',
    ],
  };

  return (
    <>
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full min-h-[620px] md:min-h-[700px] flex items-center">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20product%20catalog%20flatlay%20with%20neutral%20beige%20and%20cream%20tones%2C%20soft%20natural%20light%2C%20elegant%20fashion%20accessories%20and%20lifestyle%20items%20arranged%20artistically%20on%20light%20wood%20surface%2C%20Scandinavian%20aesthetic%2C%20editorial%20product%20photography%20with%20gentle%20shadows%2C%20clean%20composition%2C%20warm%20ambient%20mood&width=1920&height=1080&seq=hero-bg-main&orientation=landscape"
              alt="OOPBuy Spreadsheet Produktübersicht"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/30"></div>
          </div>

          <div className="relative z-10 w-full px-4 md:px-6 pt-24 md:pt-28">
            <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20 max-w-7xl mx-auto">
              <div className="flex-1 text-center lg:text-left">
                <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight tracking-tight">
                  OOPBuy Spreadsheet
                  <br />
                  <span className="text-primary-300">Die komplette</span>
                  <br />
                  <span className="text-background-50">Übersicht für Deutschland</span>
                </h1>
                <p className="mt-6 text-base md:text-lg text-background-100/90 max-w-xl leading-relaxed">
                  {t('hero_subtitle')}
                </p>

                <div className="mt-8 flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="w-full sm:w-auto px-8 py-3.5 text-base font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    oopbuy spreadsheet
                    <i className="ri-arrow-right-up-line ml-2"></i>
                  </a>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="w-full sm:w-auto px-8 py-3.5 text-base font-medium border-2 border-background-50 text-background-50 rounded-full hover:bg-background-50 hover:text-foreground-950 transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    buy it now
                    <i className="ri-shopping-bag-line ml-2"></i>
                  </a>
                </div>

                <div className="mt-8 flex flex-wrap items-center gap-4 justify-center lg:justify-start">
                  {[
                    { icon: 'ri-check-line', text: t('trust_1') },
                    { icon: 'ri-check-line', text: t('trust_2') },
                    { icon: 'ri-check-line', text: t('trust_3') },
                    { icon: 'ri-check-line', text: t('trust_4') },
                  ].map((item) => (
                    <span key={item.text} className="flex items-center gap-1.5 text-xs text-background-100/80">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className={`${item.icon} text-accent-400 text-xs`}></i>
                      </span>
                      {item.text}
                    </span>
                  ))}
                </div>
              </div>

              <div className="hidden lg:block flex-1">
                <div className="bg-background-50/10 backdrop-blur-sm rounded-2xl p-6 border border-background-50/20">
                  <img
                    src="https://readdy.ai/api/search-image?query=Clean%20minimal%20spreadsheet%20dashboard%20interface%20with%20product%20listings%20and%20price%20tables%2C%20warm%20neutral%20color%20scheme%2C%20modern%20SaaS%20UI%20design%2C%20soft%20lighting%2C%20elegant%20data%20visualization%2C%20clean%20typography%2C%20professional%20layout&width=800&height=600&seq=hero-card-dashboard&orientation=landscape"
                    alt="OOPBuy Spreadsheet Interface"
                    className="w-full rounded-xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Was ist OOPBuy Spreadsheet */}
        <section className="py-20 md:py-28 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row items-center gap-14">
              <div className="flex-1">
                <span className="inline-block px-3 py-1 text-xs font-medium bg-accent-100 text-accent-800 rounded-full mb-4">
                  Grundlagen
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 tracking-tight">
                  {t('about_title')}
                </h2>
                <p className="mt-5 text-base text-foreground-600 leading-relaxed max-w-lg">
                  Das <strong>OOPBuy Spreadsheet</strong> ist eine umfassende, strukturierte Produktdatenbank, die speziell für den deutschen Markt entwickelt wurde. Es vereint Produktlisten, Preisvergleiche und Kategorieübersichten in einem einfach nutzbaren Format. Anders als eine normale Suche bietet das Spreadsheet eine kuratierte Auswahl der besten Produkte – übersichtlich, aktuell und komplett auf Deutsch.
                </p>
                <p className="mt-4 text-base text-foreground-600 leading-relaxed max-w-lg">
                  Egal ob du nach Sneakern, Fashion, Streetwear oder Accessoires suchst – das OOPBuy Spreadsheet macht die Produktrecherche schneller, einfacher und sicherer. Kein langes Suchen, keine unübersichtlichen Listen: Alles, was du brauchst, an einem Ort.
                </p>
                <Link
                  to="/oopbuy-spreadsheet-guide"
                  className="inline-flex items-center gap-2 mt-6 px-6 py-3 text-sm font-medium bg-accent-500 text-background-50 rounded-full hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {t('read_more')}
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </Link>

                <div className="mt-10 flex gap-12">
                  <div>
                    <p className="font-heading text-3xl font-bold text-foreground-950">14+</p>
                    <p className="text-sm text-foreground-500 mt-1">Kategorien</p>
                  </div>
                  <div>
                    <p className="font-heading text-3xl font-bold text-foreground-950">100%</p>
                    <p className="text-sm text-foreground-500 mt-1">Kostenlos</p>
                  </div>
                </div>
              </div>

              <div className="flex-1">
                <div className="relative rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Elegant%20minimalist%20product%20display%20with%20curated%20fashion%20items%20on%20a%20warm%20neutral%20background%2C%20soft%20diffused%20lighting%2C%20Scandinavian%20interior%20style%2C%20clean%20aesthetic%2C%20high-end%20editorial%20product%20photography%2C%20light%20beige%20and%20cream%20tones%2C%20gentle%20composition&width=800&height=600&seq=about-section-img&orientation=landscape"
                    alt="OOPBuy Spreadsheet Produktauswahl"
                    className="w-full rounded-2xl"
                  />
                  <div className="absolute top-4 left-4 px-3 py-1.5 bg-background-50/90 backdrop-blur-sm rounded-full">
                    <span className="text-xs font-medium text-foreground-800">Deutschland</span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-6">
                    <p className="text-sm text-background-50 font-medium">Kuratierte Produktauswahl</p>
                    <p className="text-xs text-background-100/80 mt-1">Für den deutschen Markt optimiert</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Warum ist OOPBuy Spreadsheet besser */}
        <section className="py-20 md:py-28 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <span className="inline-block px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-800 rounded-full mb-4">
                Vergleich
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 tracking-tight">
                {t('why_title')}
              </h2>
              <p className="mt-4 text-base text-foreground-600 max-w-2xl mx-auto">
                Nicht alle Methoden zum Produkte-Finden sind gleich effizient. Hier siehst du, warum das OOPBuy Spreadsheet die beste Wahl für dich ist.
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b-2 border-background-300">
                    <th className="py-4 px-6 text-sm font-semibold text-foreground-800">Methode</th>
                    <th className="py-4 px-6 text-sm font-semibold text-foreground-800">Übersicht</th>
                    <th className="py-4 px-6 text-sm font-semibold text-foreground-800">Geschwindigkeit</th>
                    <th className="py-4 px-6 text-sm font-semibold text-foreground-800">Anfängerfreundlich</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-background-200/70 bg-background-50/50">
                    <td className="py-4 px-6 text-sm font-semibold text-primary-700">OOPBuy Spreadsheet</td>
                    <td className="py-4 px-6 text-sm text-foreground-700">★★★★★</td>
                    <td className="py-4 px-6 text-sm text-foreground-700">Sehr schnell</td>
                    <td className="py-4 px-6">
                      <span className="inline-block px-3 py-1 text-xs font-medium bg-accent-100 text-accent-800 rounded-full">Ja</span>
                    </td>
                  </tr>
                  <tr className="border-b border-background-200/70">
                    <td className="py-4 px-6 text-sm font-medium text-foreground-700">Normale Suche</td>
                    <td className="py-4 px-6 text-sm text-foreground-700">★★★</td>
                    <td className="py-4 px-6 text-sm text-foreground-700">Mittel</td>
                    <td className="py-4 px-6">
                      <span className="inline-block px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-800 rounded-full">Teilweise</span>
                    </td>
                  </tr>
                  <tr className="border-b border-background-200/70">
                    <td className="py-4 px-6 text-sm font-medium text-foreground-700">Einzelne Links</td>
                    <td className="py-4 px-6 text-sm text-foreground-700">★★</td>
                    <td className="py-4 px-6 text-sm text-foreground-700">Langsam</td>
                    <td className="py-4 px-6">
                      <span className="inline-block px-3 py-1 text-xs font-medium bg-foreground-100 text-foreground-600 rounded-full">Nein</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* So funktioniert es */}
        <section className="py-20 md:py-28 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <span className="inline-block px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full mb-4">
                Schritt für Schritt
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 tracking-tight">
                {t('how_title')}
              </h2>
            </div>

            <div className="relative">
              <div className="hidden lg:block absolute top-1/2 left-[10%] right-[10%] h-px bg-background-300 -translate-y-1/2"></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8">
                {[
                  { step: '1', title: 'Spreadsheet öffnen', desc: 'Rufe das OOPBuy Spreadsheet auf und verschaffe dir einen ersten Überblick über die verfügbaren Kategorien.', icon: 'ri-folder-open-line' },
                  { step: '2', title: 'Produkt auswählen', desc: 'Durchsuche die Kategorien und finde genau das Produkt, das du suchst – von Sneakern bis Accessoires.', icon: 'ri-search-line' },
                  { step: '3', title: 'Preis vergleichen', desc: 'Vergleiche Preise und Konditionen verschiedener Anbieter direkt im Spreadsheet.', icon: 'ri-scales-line' },
                  { step: '4', title: 'Details prüfen', desc: 'Überprüfe Produktdetails, Bewertungen und Verfügbarkeit, bevor du eine Entscheidung triffst.', icon: 'ri-file-list-3-line' },
                  { step: '5', title: 'Sicher bestellen', desc: 'Navigiere zum gewünschten Produkt und schließe deinen Kauf sicher und informiert ab.', icon: 'ri-shield-check-line' },
                ].map((item, idx) => (
                  <div key={item.step} className="relative text-center group">
                    <div className="w-16 h-16 mx-auto bg-background-100 rounded-full flex items-center justify-center group-hover:bg-primary-500 transition-colors duration-300">
                      <span className="w-6 h-6 flex items-center justify-center">
                        <i className={`${item.icon} text-xl text-primary-500 group-hover:text-background-50 transition-colors duration-300`}></i>
                      </span>
                    </div>
                    <span className="absolute top-0 right-4 lg:right-auto lg:left-4 w-6 h-6 bg-primary-500 text-background-50 rounded-full flex items-center justify-center text-xs font-bold">
                      {item.step}
                    </span>
                    <h3 className="mt-4 text-sm font-semibold text-foreground-900">{item.title}</h3>
                    <p className="mt-2 text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SEO Artikel Grid - 9 Artikel */}
        <section className="py-20 md:py-28 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <span className="inline-block px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full mb-4">
                SEO Ratgeber
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 tracking-tight">
                OOPBuy Spreadsheet – Alle Ratgeber
              </h2>
              <p className="mt-4 text-base text-foreground-600 max-w-2xl mx-auto">
                Entdecke unsere ausführlichen SEO-Ratgeber rund um das OOPBuy Spreadsheet. Von Sicherheit über Preisvergleich bis zu Kategorie-Guides.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {articles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/${article.slug}`}
                  className="group bg-background-50 rounded-2xl overflow-hidden hover:shadow-md transition-all duration-300 cursor-pointer flex flex-col"
                >
                  <div className="relative overflow-hidden aspect-[16/10]">
                    <img
                      src={article.cardImage}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>
                    <div className="absolute top-3 left-3">
                      <span className="px-2.5 py-1 text-xs font-medium bg-background-50/90 backdrop-blur-sm text-foreground-800 rounded-full">
                        {article.readTime}
                      </span>
                    </div>
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors leading-snug line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="mt-2 text-xs text-foreground-500 leading-relaxed line-clamp-2 flex-1">
                      {article.subtitle}
                    </p>
                    <div className="mt-4 flex items-center justify-between">
                      <span className="text-xs text-foreground-400 flex items-center gap-1">
                        <span className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-calendar-line text-xs"></i>
                        </span>
                        {new Date(article.datePublished).toLocaleDateString('de-DE', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                      <span className="flex items-center gap-1 text-xs font-medium text-primary-500 group-hover:text-primary-600 transition-colors">
                        Weiterlesen
                        <span className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-arrow-right-line text-xs"></i>
                        </span>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Kategorie Grid */}
        <section className="py-20 md:py-28 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <span className="inline-block px-3 py-1 text-xs font-medium bg-accent-100 text-accent-800 rounded-full mb-4">
                Entdecken
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 tracking-tight">
                {t('categories_title')}
              </h2>
              <p className="mt-4 text-base text-foreground-600 max-w-2xl mx-auto">
                Durchstöbere alle Produktkategorien des OOPBuy Spreadsheets und finde genau das, wonach du suchst.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {categories.map((cat, idx) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className={`group relative rounded-xl overflow-hidden cursor-pointer ${
                    idx === 0 ? 'sm:col-span-2 sm:row-span-2' : 'aspect-square'
                  }`}
                >
                  <div className={idx === 0 ? 'aspect-[4/3] sm:aspect-auto sm:h-full' : 'aspect-square'}>
                    <img
                      src={cat.image}
                      alt={cat.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-sm font-semibold text-background-50">{cat.name}</h3>
                  </div>
                </a>
              ))}
            </div>

            <div className="text-center mt-10">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-7 py-3 text-sm font-medium border-2 border-foreground-300 text-foreground-700 rounded-full hover:bg-foreground-950 hover:text-background-50 hover:border-foreground-950 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Alle Kategorien entdecken
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </span>
              </a>
            </div>
          </div>
        </section>

        {/* Sicherheit */}
        <section className="py-20 md:py-28 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row items-center gap-14">
              <div className="flex-1 order-2 lg:order-1">
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Minimalist%20security%20concept%20with%20shield%20symbol%20on%20warm%20neutral%20background%2C%20soft%20lighting%2C%20clean%20aesthetic%2C%20professional%20trust%20visual%2C%20elegant%20composition%2C%20reassuring%20warm%20tones%2C%20Scandinavian%20design&width=800&height=600&seq=security-section-img&orientation=landscape"
                    alt="OOPBuy Spreadsheet Sicherheit"
                    className="w-full rounded-2xl"
                  />
                </div>
              </div>

              <div className="flex-1 order-1 lg:order-2">
                <span className="inline-block px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-800 rounded-full mb-4">
                  Vertrauen
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 tracking-tight">
                  {t('security_title')}
                </h2>
                <p className="mt-5 text-base text-foreground-600 leading-relaxed max-w-lg">
                  Sicherheit hat beim Online-Shopping höchste Priorität. Das OOPBuy Spreadsheet wurde entwickelt, um dir eine vertrauenswürdige Grundlage für deine Kaufentscheidungen zu bieten. Es enthält ausschließlich geprüfte und bewährte Quellen.
                </p>

                <div className="mt-8 space-y-4">
                  {[
                    { title: 'Datenschutz', desc: 'Keine Erfassung persönlicher Daten – du surfst vollkommen anonym.' },
                    { title: 'Geprüfte Quellen', desc: 'Alle aufgelisteten Produkte stammen von verifizierten Plattformen.' },
                    { title: 'Sichere Nutzung', desc: 'Das Spreadsheet selbst ist frei von Schadsoftware und Trackern.' },
                  ].map((item) => (
                    <div key={item.title} className="flex gap-3">
                      <span className="w-5 h-5 flex-shrink-0 rounded-full bg-accent-100 flex items-center justify-center mt-0.5">
                        <i className="ri-check-line text-xs text-accent-600"></i>
                      </span>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-900">{item.title}</h4>
                        <p className="text-xs text-foreground-500 mt-0.5">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-8 overflow-x-auto">
                  <table className="w-full text-left border-collapse text-sm">
                    <thead>
                      <tr className="border-b border-background-300">
                        <th className="py-3 pr-4 text-xs font-semibold text-foreground-800">Risiko</th>
                        <th className="py-3 pl-4 text-xs font-semibold text-foreground-800">Lösung</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { risk: 'Fake Webseiten', solution: 'Nur geprüfte Quellen nutzen' },
                        { risk: 'Betrug', solution: 'Bewertungen vor dem Kauf prüfen' },
                        { risk: 'Schlechte Qualität', solution: 'Produktbilder genau analysieren' },
                      ].map((row) => (
                        <tr key={row.risk} className="border-b border-background-200/70">
                          <td className="py-3 pr-4 text-foreground-700">{row.risk}</td>
                          <td className="py-3 pl-4 text-foreground-600">{row.solution}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Nutzer Vorteile + CTA */}
        <section className="py-20 md:py-28 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: 'ri-time-line', title: 'Spart Zeit', desc: 'Finde Produkte in Sekunden statt Minuten – die kuratierte Liste macht es möglich.' },
                { icon: 'ri-layout-grid-line', title: 'Bessere Übersicht', desc: 'Alle Kategorien und Produkte strukturiert an einem Ort, ohne lästiges Hin- und Herspringen.' },
                { icon: 'ri-money-euro-circle-line', title: 'Preisvergleich', desc: 'Vergleiche Preise verschiedener Anbieter direkt im Spreadsheet und spare bares Geld.' },
                { icon: 'ri-user-smile-line', title: 'Anfängerfreundlich', desc: 'Keine Vorkenntnisse nötig – das Spreadsheet ist intuitiv und selbsterklärend aufgebaut.' },
              ].map((item) => (
                <div key={item.title} className="bg-background-50 rounded-xl p-6 text-center">
                  <span className="w-12 h-12 mx-auto bg-primary-100 rounded-full flex items-center justify-center">
                    <i className={`${item.icon} text-lg text-primary-500`}></i>
                  </span>
                  <h3 className="mt-4 text-sm font-semibold text-foreground-900">{item.title}</h3>
                  <p className="mt-2 text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-14 text-center">
              <Link
                to="/oopbuy-spreadsheet-guide"
                className="inline-flex items-center gap-2 px-7 py-3.5 text-sm font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Komplette OOPBuy Spreadsheet Anleitung lesen
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </span>
              </Link>
            </div>
          </div>
        </section>

        {/* CTA Section - Full screen */}
        <section className="relative w-full min-h-[500px] md:min-h-[600px] flex items-center justify-center">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20toned%20elegant%20product%20flatlay%20with%20fashion%20accessories%20and%20lifestyle%20items%20on%20light%20wood%2C%20soft%20natural%20morning%20light%2C%20minimalist%20composition%2C%20Scandinavian%20aesthetic%2C%20cream%20and%20beige%20tones%2C%20artistic%20product%20photography%2C%20gentle%20shadows%2C%20inviting%20atmosphere&width=1920&height=1080&seq=cta-fullscreen-bg&orientation=landscape"
              alt="OOPBuy Spreadsheet"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/45"></div>
          </div>

          <div className="relative z-10 text-center px-4 md:px-6 max-w-3xl mx-auto">
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-background-50 tracking-tight">
              BEREIT FÜR DEIN NÄCHSTES SHOPPING?
            </h2>
            <p className="mt-4 text-base md:text-lg text-background-100/90 leading-relaxed">
              Entdecke die volle Produktvielfalt auf Kaspreadsheet
              <br />
              und finde genau das, wonach du suchst.
            </p>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-3 mt-8 px-8 py-4 bg-foreground-950 text-background-50 rounded-full hover:bg-foreground-900 transition-colors cursor-pointer group"
            >
              <span className="w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center">
                <i className="ri-shopping-bag-line text-background-50"></i>
              </span>
              <span className="text-base font-semibold whitespace-nowrap">ZUR HAUPTWEBSITE</span>
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-right-up-line text-background-50"></i>
              </span>
            </a>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 md:py-28 px-4 md:px-6 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 text-xs font-medium bg-accent-100 text-accent-800 rounded-full mb-4">
                Fragen & Antworten
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 tracking-tight">
                {t('faq_title')}
              </h2>
            </div>

            <div className="bg-background-100 rounded-2xl p-6 md:p-8">
              {faqs.map((faq) => (
                <FAQItem key={faq.q} question={faq.q} answer={faq.a} />
              ))}
            </div>
          </div>

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
          />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }}
          />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
          />
        </section>

        {/* Letzter CTA vor Footer */}
        <section className="py-16 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
              Starte jetzt mit dem OOPBuy Spreadsheet
            </h2>
            <p className="mt-3 text-base text-foreground-600 max-w-2xl mx-auto">
              Entdecke tausende Produkte, vergleiche Preise und finde die besten Deals – alles mit dem OOPBuy Spreadsheet, deiner zentralen Anlaufstelle für China Shopping aus Deutschland.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full sm:w-auto px-8 py-3.5 text-base font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                oopbuy spreadsheet
                <i className="ri-arrow-right-up-line ml-2"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full sm:w-auto px-8 py-3.5 text-base font-medium border-2 border-foreground-300 text-foreground-700 rounded-full hover:bg-foreground-950 hover:text-background-50 hover:border-foreground-950 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                buy it now
                <i className="ri-shopping-bag-line ml-2"></i>
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}